'use client';

import React, { useCallback, useEffect, useState } from "react";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";

interface CounterProps {
  title: string;
  subtitle?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Title on the left, large stepper on the right, on one line. */
  inline?: boolean;
}

const Counter: React.FC<CounterProps> = ({
  title,
  subtitle,
  value,
  onChange,
  disabled,
  inline
}) => {
  const [editableValue, setEditableValue] = useState(value);

  useEffect(() => {
    setEditableValue(value);
  }, [value]);

  const onAdd = useCallback(() => {
    if (!disabled) {
      setEditableValue(editableValue + 1);
      onChange(editableValue + 1);
    }
  }, [editableValue, onChange, disabled]);

  const onReduce = useCallback(() => {
    if (!disabled && editableValue > 0) {
      setEditableValue(editableValue - 1);
      onChange(editableValue - 1);
    }
  }, [editableValue, onChange, disabled]);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        const inputValue = parseInt(event.target.value);
        if (!isNaN(inputValue)) {
          setEditableValue(inputValue);
          onChange(inputValue);
        }
      }
    },
    [onChange, disabled]
  );

  if (inline) {
    const stepButton =
      "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-white text-cyan-800 transition hover:border-cyan-700 active:bg-cyan-50 disabled:opacity-40 disabled:hover:border-neutral-300";
    return (
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-neutral-900">{title}</div>
          {subtitle && (
            <div className="text-sm font-light text-gray-600">{subtitle}</div>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-row items-center gap-1">
          <button
            type="button"
            aria-label={`Fewer: ${title}`}
            onClick={onReduce}
            disabled={disabled || editableValue <= 0}
            className={stepButton}
          >
            <AiOutlineMinus size={16} />
          </button>
          <input
            type="number"
            min="0"
            aria-label={title}
            value={editableValue}
            onChange={onInputChange}
            className="h-9 w-9 bg-transparent text-center text-base font-bold text-neutral-900 [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            inputMode="numeric"
            disabled={disabled}
          />
          <button
            type="button"
            aria-label={`More: ${title}`}
            onClick={onAdd}
            disabled={disabled}
            className={stepButton}
          >
            <AiOutlinePlus size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex sm:flex-row flex-col sm:items-center justify-between sm:ml-0 ml-2">
      <div className="flex flex-col">
        <div className="font-medium text-sm sm:mr-2">{title}</div>
        {subtitle && (
          <div className="font-light text-sm text-gray-600 sm:mr-2 mt-1 mb-1">{subtitle}</div>
        )}
      </div>
      <div className="flex flex-row items-center sm:gap-2 gap-3">
        <div
          onClick={onReduce}
          className="sm:w-6 sm:h-6 w-5 h-5 rounded-full border-[1px] border-neutral-400 flex items-center justify-center text-neutral-600 cursor-pointer hover:opacity-80 transition"
        >
          <AiOutlineMinus size={16} />
        </div>
        <input
          type="number"
          min="0"
          value={editableValue}
          onChange={onInputChange}
          className="sm:w-12 sm:h-7 w-10 h-6 rounded-md border-[1px] border-neutral-400 text-center text-neutral-600 appearance-none"
          inputMode="numeric"
          disabled={disabled}
        />
        <div
          onClick={onAdd}
          className="sm:w-6 sm:h-6 w-5 h-5 rounded-full border-[1px] border-neutral-400 flex items-center justify-center text-neutral-600 cursor-pointer hover:opacity-80 transition"
        >
          <AiOutlinePlus size={16} />
        </div>
      </div>
    </div>
  );
};

export default Counter;
